(ns splash.ui.lender-portal.views.pages.application
  (:require
    ["@azure/msal-react" :refer [useMsal]]
    ["react-router-dom" :refer [useParams]]
    [goog.string :refer [format]]
    [reagent.core :as r]
    [splash.ui.lender-portal.common.auth :as auth]
    [splash.ui.lender-portal.common.request :as request]
    [splash.ui.lender-portal.common.util :as util]
    [splash.ui.lender-portal.components.application-banner :refer [application-banner]]
    [splash.ui.lender-portal.components.layout :as layout]
    [splash.ui.lender-portal.components.mui :as mui]
    [splash.ui.lender-portal.components.nav-supporting-pages :refer [supporting-pages]]
    [splash.ui.lender-portal.components.search-box :refer [search-box]]
    [splash.ui.lender-portal.components.section :refer [section]]
    [splash.ui.lender-portal.components.theme :as theme]
    [splash.ui.lender-portal.datadog :as datadog]
    [splash.ui.lender-portal.feature-flags :as feature-flags]
    [splash.ui.lender-portal.optimizely :as optimizely]
    [splash.ui.lender-portal.state :as state]))

(defn is-personal-loan?
  [application]
  (= "Personal Loan" (:product-type application)))

(defn is-student-loan?
  [application]
  (= "Student Loan Refinance" (:product-type application)))

(defn is-first-tech?
  [application]
  (contains? #{"First Tech" "First Tech Personal Loans"} (get application :rate-source)))

;; These paths are subject to change
;; pending completion of data sourcing schemas
(def fields
  [{:section "Loan Summary" :label "Original Loan Amount" :path [:original-loan-amount] :type :money}
   {:section "Loan Summary" :label "Original Selected Rate" :path [:original-selected-rate-bp] :type :rate}
   {:section "Loan Summary" :label "Original Term (years)" :path [:original-selected-term]}
   {:section "Loan Summary" :label "Original DTI" :path [:original-selected-rate-dti] :type :percentage}
   {:section "Loan Summary" :label "Final Loan Amount" :path [:final-loan-amount] :type :money}
   {:section "Loan Summary" :label "Final Selected Rate" :path [:final-selected-rate-bp]  :type :rate}
   {:section "Loan Summary" :label "Final Term (years)" :path [:final-selected-rate-term]}
   {:section "Loan Summary" :label "Final DTI" :value (fn [a]
                                                        (or
                                                          (:final-selected-rate-dti a)
                                                          (:latest-credit-risk-dti a))) :type :percentage}
   {:section "Loan Summary" :label "Assigned Underwriter" :path [:assigned-underwriter-name]}
   {:section "Loan Summary" :label "Selected Rate Risk Tier" :value (fn [a]
                                                                      (or
                                                                        (:final-selected-rate-risk-tier a)
                                                                        (:original-selected-rate-risk-tier a)))}
   {:section "Loan Summary" :label (fn [a]
                                     (if (is-personal-loan? a)
                                       "Self-Attested Gross Income"
                                       "Annual Gross Income")) :path [:self-attested-gross-income] :type :money}
   {:section "Loan Summary" :label "Verified Gross Income" :path [:verified-gross-income] :type :money :pred is-personal-loan?}
   {:section "Loan Summary" :label "Loan Purpose" :path [:loan-purpose] :pred is-personal-loan?}
   {:section "Loan Summary" :label "Un-Refinanced Monthly Payment" :path [:unrefinanced-monthly-payment] :type :money :pred is-student-loan?}
   {:section "Loan Summary" :label "Additional Liabilities" :path [:additional-monthly-liabilities] :type :money}
   {:section "Loan Summary" :label "Approval Date" :path [:approved-date] :type :datetime}
   {:section "Loan Summary" :label "FICO 8" :path [:fico-8]}
   {:section "Loan Summary" :label "FICO 9" :path [:fico-9]}
   {:section "Loan Summary" :label "Degree" :path [:degree] :pred is-student-loan?}
   {:section "Loan Summary" :label "School" :path [:school] :pred is-student-loan?}
   {:section "Borrower Info" :label "First Name" :path [:first-name]}
   {:section "Borrower Info" :label "Last Name" :path [:last-name]}
   {:section "Borrower Info" :label "Date of Birth" :path [:date-of-birth]}
   {:section "Borrower Info" :label "Email" :path [:email]}
   {:section "Borrower Info" :label "Current Address 1" :path [:current-address-1]}
   {:section "Borrower Info" :label "Current City" :path [:current-city]}
   {:section "Borrower Info" :label "Current State" :path [:current-state]}
   {:section "Borrower Info" :label "Current Zip" :path [:current-zip]}
   {:section "Borrower Info" :label "SSN" :path [:encrypted-ssn-pii]}
   {:section "Membership Info" :label "Lender Application ID" :path [:lender-application-id]}
   {:section "Membership Info" :label "Splash Application ID" :path [:application-id]}
   {:section "Membership Info" :label "Membership Status" :path [:lender-memberization-status]}
   {:section "Income" :label "Income Verified Date" :path [:income-verified-date] :type :datetime}
   {:section "Income" :label "Income Verified By" :path [:income-verified-by]}
   {:section "Income" :label (fn [a]
                               (if (is-personal-loan? a)
                                 "Self-Attested Gross Income"
                                 "Annual Gross Income")) :path [:self-attested-gross-income] :type :money}
   {:section "Income" :label "Verified Gross Income" :path [:verified-gross-income] :type :money :pred is-personal-loan?}
   {:section "Income" :label "Housing Status" :path [:housing-status]}
   {:section "Income" :label "Housing Monthly Payment" :path [:housing-monthly-payment] :type :money}
   {:section "Income" :label "Housing Comment" :path [:housing-comment]}
   {:section "Income" :label "Employment Status" :path [:employment-status]}
   {:section "Income" :label "Employer Name" :path [:employer-name]}
   {:section "Income" :label "Start Date" :path [:employment-start-date]}
   {:section "Income" :label "Occupation" :path [:occupation]}
   {:section "Identity" :label "Identity Verified Date" :path [:identity-verified-date] :type :datetime}
   {:section "Identity" :label "Identity Verified By" :path [:identity-verified-by]}
   {:section "Identity" :label "Fraud Verified Date" :path [:fraud-verified-at] :type :datetime}
   {:section "Identity" :label "Fraud Verified By" :path [:fraud-verified-by]}
   {:section "Identity" :label "Credit Verified Date" :path [:credit-verified-at] :type :datetime}
   {:section "Identity" :label "Credit Verified By" :path [:credit-verified-by]}
   {:section "Identity" :label "ID Type" :path [:identification-type]}
   {:section "Identity" :label "ID Number" :path [:identification-number]}
   {:section "Identity" :label "Issued By" :path [:identification-state]}
   {:section "Identity" :label "Issued Date" :path [:identification-issue-date]}
   {:section "Identity" :label "Expiration Date" :path [:identification-expiration-date]}
   {:section "Liabilities" :label "Liabilities Verified Date" :path [:liabilities-verified-date] :type :datetime}
   {:section "Liabilities" :label "Liabilities Verified By" :path [:liabilities-verified-by]}
   {:section "Liabilities" :label "Loan Type" :path [:loan-type]}
   {:section "Education" :label "Education Verified Date" :path [:education-verified-date] :type :datetime}
   {:section "Education" :label "Education Verified By" :path [:education-verified-by]}
   {:section "Education" :label "Degree" :path [:degree]}
   {:section "Education" :label "School" :path [:school]}
   {:section "Education" :label "Graduation Month" :path [:graduation-month]}
   {:section "Education" :label "Graduation Year" :path [:graduation-year]}
   {:section "Education" :label "Major" :path [:major]}
   {:section "Education" :label "Undergraduate School" :path [:undergraduate-school]}
   {:section "Education" :label "Associates Experience" :path [:associates-experience]}
   {:section "Education" :label "Is Travel PT" :path [:is-travel-pt]}
   {:section "Education" :label "Resident or Fellow" :path [:resident-or-fellow]}
   {:section "NSC Degree Data" :label "Highest Degree Level Completed" :path [:nsc-highest-degree-completed]}
   {:section "NSC Degree Data" :label "Notified Date" :path [:nsc-notified-date]}
   {:section "Application Dates" :label "Loan Approved Date" :path [:approved-date] :type :date}
   ;; The labels don't match the selected date on purpose for "Credit Agreement Presented" and "Esign Date"
   ;; because the labels are meant to represent when the status was completed
   ;; For example, `right-to-cancel-date` is labeled "Esign Date"
   ;; because that date is when the user completed esigning.
   {:section "Application Dates" :label "Credit Agreement Presented" :path [:esign-date] :type :date}
   {:section "Application Dates" :label "Esign Date" :path [:right-to-cancel-date] :type :date}
   {:section "Application Dates" :label "Pending Disbursement Date" :path [:pending-disbursement-date] :type :date}
   {:section "Application Dates" :label "Fully Disbursed Date" :path [:fully-disbursed-date] :type :date}
   {:section "Application Dates" :label "Closed Date" :path [:closed-date] :type :date}
   {:section "Application Dates" :label "Loan Denied Date" :path [:loan-denied-date] :type :date}
   {:section "Application Dates" :label "Withdrawn Date" :path [:withdrawn-date] :type :date}
   {:section "Application Dates" :label "Expired Date" :path [:uw-expired-date] :type :date}
   {:section "Application Dates" :label "Expired Loan Offer Date" :path [:expired-loan-offer-date] :type :date}])

(defn field-format-fn
  [type]
  (case type
    :rate (fn [v]
            (when (int? v)
              (str (/ v 100) "%")))
    :date util/format-datetime-str
    :datetime util/format-datetime-str
    :percentage (fn [v]
                  (when (number? v)
                    (str (format "%.2f" (* v 100)) "%")))
    identity))

(defn field-grid-item
  [application {:keys [label path value type pred]}]
  [mui/grid {:item true
             :xs 3}
   (let [show? (if (fn? pred)
                 (pred application)
                 true)]
     (when show?
       [mui/text-field {:label (if (fn? label)
                                 (label application)
                                 label)
                        :default-value (let [value (if (fn? value)
                                                     (value application)
                                                     (get-in application path))
                                             formatter (field-format-fn type)]
                                         (try
                                           (formatter value)
                                           (catch ExceptionInfo ex
                                             (.error js/console ex)
                                             value)))
                        :size "small"
                        :full-width true
                        :InputProps {:readOnly true
                                     :startAdornment (when (= :money type)
                                                       (r/as-element [mui/input-adornment
                                                                      {:position :start}
                                                                      "$"]))}
                        ;; This shrink prop forces the label 
                        ;; to the top of the input even when empty.
                        ;; Otherwise, the label sits inside the input
                        ;; as a placeholder
                        :InputLabelProps {:shrink true}}]))])

(defn application-detail-fields
  [application fields]
  [mui/grid {:container true
             :spacing 2
             :sx {:pr "48px"}}
   (map (fn [field]
          ^{:key (hash field)}
          [field-grid-item application field]) fields)])

(defn application-detail-section
  [application title child]
  (let [fields (filter #(= title (:section %)) fields)]
    [section title
     [:<>
      [application-detail-fields application fields]
      child]]))

(def default-column-opts
  {:sortable false})

(def detail-table-default-props
  {:initialState {:pagination {:paginationModel {:page 0
                                                 :pageSize 100}}}
   :disableColumnFilter true
   :columnHeaderHeight 42
   :hideFooter true
   :sx {:borderWidth 0
        "& .MuiDataGrid-columnHeaders" {:borderBottom 0}
        "& .MuiDataGrid-cell" {:py "14px"
                               :backgroundColor "white"}}})

(def notes-table-columns
  (let [fields
        [{:field      "description"
          :headerName "Description"
          :width      165}
         {:field      "created-by"
          :headerName "Creator"
          :width      165}
         {:field          "created-at"
          :headerName     "Date"
          :width          165
          :valueFormatter (fn [params] (util/format-datetime-ms-str (.-value params)))}
         {:field      "contents"
          :headerName "Note"
          :flex       1}]]
    (map #(merge default-column-opts %) fields)))

(defn note-row
  [note]
  (r/with-let [created-at (util/format-datetime-ms-str (:created-at note))]
    [mui/table-row {:sx {:background-color "#FFFFFF"
                         :py               "14px"}}
     [mui/table-cell {:width 165
                      :sx    {:py "8px"
                              :px "16px"}}
      (:description note)]
     [mui/table-cell {:width 165
                      :sx    {:py "8px"
                              :px "16px"}}
      (:created-by note)]
     [mui/table-cell {:width 165
                      :sx    {:py "8px"
                              :px "16px"}}
      created-at]
     [mui/table-cell {:className "dd-privacy-mask"
                      :sx {:py "8px"
                           :px "16px"}}
      (:contents note)]]))

(defn notes-table-v2
  [application]
  (r/with-let [notes (:notes application)]
    [mui/table-container {:elevation 0
                          :sx        {:display         :flex
                                      :backgroundColor theme/section-header-background
                                      :p               "8px 24px 24px 8px"
                                      :border-radius "4px"}}
     [mui/table
      [mui/table-head
       [mui/table-row
        [mui/table-cell {:sx {:font-weight :bold}}
         "Description"]
        [mui/table-cell {:sx {:font-weight :bold}}
         "Creator"]
        [mui/table-cell {:sx {:font-weight :bold}}
         "Date"]
        [mui/table-cell {:sx {:font-weight :bold}}
         "Contents"]]]
      [mui/table-body
       (for [note notes]
         ^{:key (str (random-uuid))}
         [:<>
          [note-row note]])]]]))

(defn notes-table-datagrid
  [application]
  (r/with-let [notes-rows (->> (or (:notes application) [])
                            ;; this is temporary until we can
                            ;; figure out how to get note ids
                            (map (fn [note] (assoc note :id (str (random-uuid))))))]
    [mui/paper {:elevation 0
                :sx        {:backgroundColor theme/section-header-background
                            :pl              "24px"
                            :pb              "24px"
                            :pr              "8px"
                            :pt              "8px"}}
     [mui/data-grid (util/deep-merge detail-table-default-props
                      {:columns        notes-table-columns
                       :rows           notes-rows
                       :getRowHeight   (fn [] "auto")
                       :getRowSpacing  (fn [] 5)
                       :rowSpacingType "border"
                       :sx             {:minHeight         "300px"
                                        "& .MuiDataGrid-row" {:my "4px"}}})]]))

(defn notes-table
  [application]
  (optimizely/isFeatureEnabled-for-component
    feature-flags/notes-table
    [notes-table-v2 application]
    [notes-table-datagrid application]))

(def loans-table-columns
  (let [fields [{:field "loan-servicer"
                 :headerName "Servicer"
                 :width 180}
                {:field "loan-account-number"
                 :headerName "Account Number"
                 :width 200}
                {:field "loan-balance-to-refi"
                 :headerName "Balance to Refi"
                 :type "number"
                 :align "left"
                 :headerAlign "left"
                 :width 180
                 :valueFormatter (fn [params]
                                   (when (some? (.-value params))
                                     (str "$" (.toLocaleString (.-value params)))))}
                {:field "loan-interest-rate-bp"
                 :headerName "Interest Rate"
                 :type "number"
                 :align "left"
                 :headerAlign "left"
                 :valueFormatter (fn [params]
                                   (when (some? (.-value params))
                                     (str (/ (.-value params) 100) "%")))
                 :width 165}
                {:field "loan-last-statement-date"
                 :headerName "Last Statement Date"
                 :width 200}
                {:field "loan-source"
                 :headerName "Loan Verification"
                 :flex 1}]]
    (map #(merge default-column-opts %) fields)))

(defn loans-table
  [application]
  (let [liabilities (->> (or (:liabilities application) [])
                      ;; this is temporary until we can
                      ;; figure out how to get note ids
                      (map (fn [l]
                             (-> (assoc l :id (str (random-uuid)))))))]
    [mui/data-grid (util/deep-merge detail-table-default-props
                     {:columns loans-table-columns
                      :rows liabilities
                      :autoHeight true
                      :sx {:minHeight "auto"}})]))

(defn loan-info
  [application]
  [:<>
   [:div {:style {:margin-top "16px"
                  :margin-bottom "8px"
                  :padding-bottom "4px"
                  :border-bottom "1px solid"
                  :border-color "#DADBDB"}}
    [mui/typography {:variant :sectionTitle}
     "Loan Info"]]
   [loans-table application]])

(def nsc-degrees-columns
  (let [fields [{:field "nsc-first-name"
                 :headerName "Degree First Name"
                 :width 250}
                {:field "nsc-last-name"
                 :headerName "Degree Last Name"
                 :width 250}
                {:field "nsc-school-name"
                 :headerName "Official School Name"
                 :flex 1}]]
    (map #(merge default-column-opts %) fields)))

(defn nsc-degrees-table
  [application]
  (let [nsc-degrees (->> (or (:nsc-degrees application) [])
                      distinct
                      (map (fn [v]
                             (-> (assoc v :id (str (random-uuid)))))))]
    [mui/data-grid (util/deep-merge detail-table-default-props
                     {:columns nsc-degrees-columns
                      :rows nsc-degrees
                      :autoHeight true
                      :sx {:minHeight "auto"}})]))

(defn nsc-degree-detail
  [application]
  [:<>
   [:div {:style {:margin-top "16px"
                  :margin-bottom "24px"
                  :padding-bottom "4px"
                  :border-bottom "1px solid"
                  :border-color "#DADBDB"}}
    [mui/typography {:variant :sectionTitle}
     "NSC Degree Data"]]
   [application-detail-fields application
    (filter #(= "NSC Degree Data" (:section %)) fields)]
   [nsc-degrees-table application]])

(def additional-incomes-table-columns
  (let [fields [{:field "source"
                 :headerName "Source"
                 :width 180}
                {:field "amount"
                 :headerName "Amount"
                 :width 180
                 :valueFormatter (fn [params]
                                   (when (some? (.-value params))
                                     (str "$" (.toLocaleString (.-value params)))))}
                {:field "start-month"
                 :headerName "Start Month"
                 :width 180
                 :valueFormatter (fn [params]
                                   (let [value (.-value params)]
                                     (if (= 0 value)
                                       ""
                                       value)))}
                {:field "start-year"
                 :headerName "Start Year"
                 :width 180
                 :valueFormatter (fn [params]
                                   (let [value (.-value params)]
                                     (if (= 0 value)
                                       ""
                                       value)))}]]
    (map #(merge default-column-opts %) fields)))

(defn additional-incomes-table
  [application]
  (let [additional-incomes (->> (or (:additional-incomes application) [])
                             ;; this is temporary until we can
                             ;; figure out how to get note ids
                             (map (fn [l]
                                    (-> (assoc l :id (str (random-uuid)))))))]
    [mui/data-grid (util/deep-merge detail-table-default-props
                     {:columns additional-incomes-table-columns
                      :rows additional-incomes
                      :autoHeight true
                      :sx {:minHeight "auto"}})]))

(defn additional-incomes
  [application]
  [:<>
   [:div {:style {:margin-top "16px"
                  :margin-bottom "8px"
                  :padding-bottom "4px"
                  :border-bottom "1px solid"
                  :border-color "#DADBDB"}}
    [mui/typography {:variant :sectionTitle}
     "Additional Income Sources"]]
   [additional-incomes-table application]])

(def detail-sections
  [{:title "Loan Summary"}
   {:title "Underwriting Notes" :child notes-table}
   {:title "Borrower Info"}
   {:title "Membership Info" :pred is-first-tech?}
   {:title "Income" :child additional-incomes}
   {:title "Identity"}
   {:title "Liabilities" :child loan-info :pred is-student-loan?}
   {:title "Education" :child nsc-degree-detail :pred is-student-loan?}
   {:title "Application Dates"}])

(defn application-detail
  [current-application]
  (r/with-let [params (useParams)
               application-id (-> ^js/String (.-applicationId params)
                                parse-long)]
    (when (= application-id (:application-id @current-application))
      [mui/box {:sx {:padding "16px"}}
       [mui/stack {:spacing 6}
        (map (fn [{:keys [title child pred] :as section}]
               (when (if (some? pred) (pred @current-application) true)
                 ^{:key (hash section)}
                 [application-detail-section @current-application title
                  (when child
                    [child @current-application])]))
          detail-sections)]])))

(defn sidebar
  []
  [:<>
   [search-box]
   [supporting-pages]])

(defn view
  []
  (r/with-let [state (state/use-state)
               loading (r/cursor state [:loading])
               search-inputs (r/cursor state [:search-inputs])
               msal-instance (.-instance (useMsal))
               current-application (r/cursor state [:current-application])
               params (useParams)
               application-id (-> ^js/String (.-applicationId params)
                                parse-long)
               handle-result (fn [result]
                               (reset! loading false)
                               (reset! current-application (js->clj result)))
               mk-get-application-request (fn [application-id token]
                                            (request/mk-json-request
                                              {:uri (str "/api/v1/applications/" application-id)
                                               :token token
                                               :method :get
                                               :handler handle-result
                                               :error-handler (fn [e]
                                                                (datadog/report-error
                                                                  (str "Failed to retrieve application " application-id " from lender-portal-api")
                                                                  e
                                                                  {:application-id application-id})
                                                                (handle-result nil))}))
               on-load (fn []
                         (when (not (= application-id (:application-id @current-application)))
                           (reset! search-inputs util/initial-search-inputs)
                           (reset! loading true)
                           (-> (auth/acquire-token msal-instance)
                             (.then (partial mk-get-application-request application-id)))))]
    (r/create-class
      {:component-did-mount on-load
       :reagent-render
       (fn []
         [layout/default
          (if (= application-id (:application-id @current-application))
            [:<>
             [application-banner current-application]
             [layout/container
              [sidebar]
              [application-detail current-application]]]
            [mui/backdrop {:open @loading
                           ;; overlay should cover everything,
                           ;; but for some reason DataGrids are 
                           ;; rendering over the backdrop
                           :sx {:z-index 999}}
             [mui/circular-progress]])])})))
